import { Button, LinearProgress, Typography } from '@mui/material';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ModalBase } from '../components/ModalBase';
import { getIsOnnxUploading, getIsShowOnnxModal, getOnnxUploadProgress } from '../reducers/trainedModel/trainedModelReducer';
import { closeOnnxModalAction, uploadOnnxModalAction } from '../sagas/trainedModelSaga/trainedModelSagaActions';
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CheckIcon from '@mui/icons-material/Check';
import { PrimaryBtnSx } from '../@shared/templates';
import { Palette, Sizes } from '../@shared/palette';

export const withUploadOnnxModel = <T extends Record<string, unknown>>(
  Component: React.ComponentType<T>
): React.FC<T> => (props) => {
  const dispatch = useDispatch();
  const [file, setFile] = React.useState<File | undefined>();
  const handleClose = () => dispatch(closeOnnxModalAction())
  const onSave = () => {
    if (file) {
      dispatch(uploadOnnxModalAction(file))
    }
  }

  const isOpen = useSelector(getIsShowOnnxModal);
  const isUploading = useSelector(getIsOnnxUploading);
  const progress = useSelector(getOnnxUploadProgress);

  React.useEffect(() => {
    if (!isOpen) {
      setFile(undefined);
    }
  }, [isOpen]);

  const onChangeData = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFile(e.target.files?.[0]);
    // Allows picking the same file again after a failed upload
    e.target.value = "";
  }

  return <>
    <ModalBase
      isOpen={isOpen}
      onCancel={handleClose}
      onSave={onSave}
      title="Upload ONNX model"
      primaryBtnText='Upload'
      isDisabledSave={!file || isUploading}
      isProcessing={isUploading}
    >
      <Button
        component="label"
        variant="outlined"
        startIcon={file ? <CheckIcon /> : <UploadFileIcon />}
        disabled={isUploading}
        sx={{ ...PrimaryBtnSx, marginTop: '10px', marginBottom: "5px" }}
      >
        Select ONNX file
        <input type="file" accept=".onnx" hidden onChange={onChangeData} />
      </Button>
      <Typography sx={{
        color: Palette.textColorSecondary,
        fontSize: Sizes.textSecondary,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis"
      }}>
        {file ? `${file.name} (${(file.size / 1024 / 1024).toFixed(1)} MB)` : 'No file selected'}
      </Typography>
      {isUploading && <>
        <LinearProgress variant="determinate" value={progress} sx={{ marginTop: '10px' }} />
        <Typography sx={{ color: Palette.textColorSecondary, fontSize: Sizes.textSecondary }}>
          Uploading... {progress}%
        </Typography>
      </>}
    </ModalBase>
    <Component {...props as T} />
  </>
}
